import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns';
import { es } from 'date-fns/locale';
import { headachesAPI } from '../services/api';
import './AdvancedDashboard.css';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale
);

interface Headache {
  id: number;
  date: string;
  intensity: number;
  location: string;
  medication?: string;
  notes?: string;
  created_at: string;
}

interface AnalyticsData {
  trends: {
    labels: string[];
    intensities: number[];
    frequencies: number[];
  };
  patterns: {
    byLocation: { [key: string]: number };
    byDayOfWeek: number[];
    byTimeOfDay: number[];
    byIntensity: number[];
  };
  correlations: {
    medicationEffectiveness: { [key: string]: number };
    averageRecoveryTime: number;
    triggersAnalysis: string[];
  };
  predictions: {
    riskDays: string[];
    recommendations: string[];
  };
}

const AdvancedDashboard: React.FC = () => {
  const [headaches, setHeadaches] = useState<Headache[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(90); // días
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);

  useEffect(() => {
    loadHeadaches();
  }, [dateRange]);

  useEffect(() => {
    if (headaches.length > 0) {
      const analytics = generateAnalytics(headaches);
      setAnalyticsData(analytics);
    }
  }, [headaches]);

  const loadHeadaches = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = subDays(endDate, dateRange);
      
      const response = await headachesAPI.getAll({
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        limit: 1000
      });

      if (response.data.success) {
        setHeadaches(response.data.data.headaches || []);
      }
    } catch (error) {
      console.error('Error loading headaches for analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAnalytics = (data: Headache[]): AnalyticsData => {
    const endDate = new Date();
    const startDate = subDays(endDate, dateRange);
    const dateRange_days = eachDayOfInterval({ start: startDate, end: endDate });

    // Tendencias por día
    const trendsByDay = dateRange_days.map(day => {
      const dayString = format(day, 'yyyy-MM-dd');
      const dayHeadaches = data.filter(h => h.date === dayString);
      
      return {
        date: format(day, 'dd/MM', { locale: es }),
        intensity: dayHeadaches.length > 0 ? 
          dayHeadaches.reduce((sum, h) => sum + h.intensity, 0) / dayHeadaches.length : 0,
        frequency: dayHeadaches.length
      };
    });

    // Análisis por ubicación
    const locationCounts: { [key: string]: number } = {};
    data.forEach(h => {
      locationCounts[h.location] = (locationCounts[h.location] || 0) + 1;
    });

    // Análisis por día de la semana
    const dayOfWeekCounts = new Array(7).fill(0);
    data.forEach(h => {
      const day = new Date(h.date + 'T00:00:00').getDay();
      dayOfWeekCounts[day]++;
    });

    // Análisis por intensidad
    const intensityCounts = new Array(11).fill(0); // 0-10
    data.forEach(h => {
      intensityCounts[h.intensity]++;
    });

    // Análisis de efectividad de medicamentos
    const medicationEffectiveness: { [key: string]: number } = {};
    const medicationCounts: { [key: string]: number } = {};
    
    data.forEach(h => {
      if (h.medication && h.medication.trim()) {
        const med = h.medication.trim();
        if (!medicationEffectiveness[med]) {
          medicationEffectiveness[med] = 0;
          medicationCounts[med] = 0;
        }
        medicationEffectiveness[med] += (10 - h.intensity); // Inversamente proporcional a la intensidad
        medicationCounts[med]++;
      }
    });

    // Calcular efectividad promedio
    Object.keys(medicationEffectiveness).forEach(med => {
      const count = medicationCounts[med];
      if (count > 0) {
        medicationEffectiveness[med] = medicationEffectiveness[med] / count;
      } else {
        medicationEffectiveness[med] = 0; // Evitar división por cero
      }
    });

    // Predicciones simples basadas en patrones
    const riskDays = generateRiskPredictions(data);
    const recommendations = generateRecommendations(data);

    return {
      trends: {
        labels: trendsByDay.map(d => d.date),
        intensities: trendsByDay.map(d => d.intensity),
        frequencies: trendsByDay.map(d => d.frequency)
      },
      patterns: {
        byLocation: locationCounts,
        byDayOfWeek: dayOfWeekCounts,
        byTimeOfDay: new Array(24).fill(0), // Por implementar
        byIntensity: intensityCounts
      },
      correlations: {
        medicationEffectiveness,
        averageRecoveryTime: 0, // Por implementar
        triggersAnalysis: []
      },
      predictions: {
        riskDays,
        recommendations
      }
    };
  };

  const generateRiskPredictions = (data: Headache[]): string[] => {
    if (data.length < 7) return [];

    // Análisis simple: días de la semana con más dolores
    const dayOfWeekCounts = new Array(7).fill(0);
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    
    data.forEach(h => {
      const day = new Date(h.date + 'T00:00:00').getDay();
      dayOfWeekCounts[day]++;
    });

    const maxCount = Math.max(...dayOfWeekCounts);
    const riskThreshold = maxCount * 0.7;

    return dayOfWeekCounts
      .map((count, index) => ({ day: dayNames[index], count }))
      .filter(item => item.count >= riskThreshold)
      .map(item => item.day);
  };

  const generateRecommendations = (data: Headache[]): string[] => {
    const recommendations: string[] = [];

    if (data.length === 0) return ['No hay suficientes datos para generar recomendaciones'];

    const avgIntensity = data.reduce((sum, h) => sum + h.intensity, 0) / data.length;
    
    if (avgIntensity > 7) {
      recommendations.push('Considera consultar con un neurólogo - intensidad promedio alta');
    }

    const recentHeadaches = data.filter(h => {
      const headacheDate = new Date(h.date);
      const weekAgo = subDays(new Date(), 7);
      return headacheDate >= weekAgo;
    });

    if (recentHeadaches.length > 4) {
      recommendations.push('Frecuencia alta en la última semana - evalúa factores de estrés');
    }

    const locations = Object.entries(
      data.reduce((acc, h) => {
        acc[h.location] = (acc[h.location] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number })
    );

    const mostCommonLocation = locations.sort((a, b) => b[1] - a[1])[0];
    if (mostCommonLocation && mostCommonLocation[1] > data.length * 0.6) {
      recommendations.push(`Dolor frecuente en ${mostCommonLocation[0]} - considera terapia específica`);
    }

    if (recommendations.length === 0) {
      recommendations.push('Mantén el registro regular para mejores análisis');
    }

    return recommendations;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading) {
    return (
      <div className="advanced-dashboard loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Analizando datos...</p>
        </div>
      </div>
    );
  }

  if (!analyticsData || headaches.length === 0) {
    return (
      <div className="advanced-dashboard no-data">
        <div className="no-data-message">
          <h3>📊 Dashboard Analítico</h3>
          <p>No hay suficientes datos para generar análisis.</p>
          <p>Registra algunos dolores de cabeza para ver estadísticas avanzadas.</p>
        </div>
      </div>
    );
  }

  const trendData = {
    labels: analyticsData.trends.labels,
    datasets: [
      {
        label: 'Intensidad Promedio',
        data: analyticsData.trends.intensities,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1,
        yAxisID: 'y',
      },
      {
        label: 'Frecuencia Diaria',
        data: analyticsData.trends.frequencies,
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.1,
        yAxisID: 'y1',
      },
    ],
  };

  const locationData = {
    labels: Object.keys(analyticsData.patterns.byLocation),
    datasets: [
      {
        data: Object.values(analyticsData.patterns.byLocation),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
        ],
      },
    ],
  };

  const dayOfWeekData = {
    labels: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    datasets: [
      {
        label: 'Dolores por día de la semana',
        data: analyticsData.patterns.byDayOfWeek,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="advanced-dashboard">
      <div className="dashboard-header">
        <h2>📊 Dashboard Analítico Avanzado</h2>
        <div className="date-range-selector">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(parseInt(e.target.value))}
          >
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
            <option value={180}>Últimos 6 meses</option>
            <option value={365}>Último año</option>
          </select>
        </div>
      </div>

      <div className="analytics-grid">
        {/* Tendencias */}
        <div className="analytics-card chart-card">
          <h3>📈 Tendencias de Intensidad y Frecuencia</h3>
          <div className="chart-container">
            <Line data={trendData} options={{
              ...chartOptions,
              scales: {
                y: {
                  type: 'linear',
                  display: true,
                  position: 'left',
                  max: 10,
                  beginAtZero: true,
                },
                y1: {
                  type: 'linear',
                  display: true,
                  position: 'right',
                  beginAtZero: true,
                  grid: {
                    drawOnChartArea: false,
                  },
                },
              },
            }} />
          </div>
        </div>

        {/* Ubicaciones más frecuentes */}
        <div className="analytics-card chart-card">
          <h3>📍 Ubicaciones más frecuentes</h3>
          <div className="chart-container">
            <Doughnut data={locationData} options={chartOptions} />
          </div>
        </div>

        {/* Patrones por día de la semana */}
        <div className="analytics-card chart-card">
          <h3>📅 Patrones por día de la semana</h3>
          <div className="chart-container">
            <Bar data={dayOfWeekData} options={chartOptions} />
          </div>
        </div>

        {/* Estadísticas clave */}
        <div className="analytics-card stats-card">
          <h3>📈 Estadísticas Clave</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{headaches.length}</div>
              <div className="stat-label">Total episodios</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {headaches.length > 0 
                  ? (headaches.reduce((sum, h) => sum + h.intensity, 0) / headaches.length).toFixed(1)
                  : '0.0'
                }
              </div>
              <div className="stat-label">Intensidad promedio</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {Math.max(...analyticsData.patterns.byIntensity)}
              </div>
              <div className="stat-label">Intensidad máxima</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {dateRange > 0 
                  ? (headaches.length / (dateRange / 7)).toFixed(1)
                  : '0.0'
                }
              </div>
              <div className="stat-label">Por semana</div>
            </div>
          </div>
        </div>

        {/* Efectividad de medicamentos */}
        {Object.keys(analyticsData.correlations.medicationEffectiveness).length > 0 && (
          <div className="analytics-card">
            <h3>💊 Efectividad de Medicamentos</h3>
            <div className="medication-list">
              {Object.entries(analyticsData.correlations.medicationEffectiveness)
                .sort((a, b) => b[1] - a[1])
                .map(([medication, effectiveness]) => (
                  <div key={medication} className="medication-item">
                    <span className="medication-name">{medication}</span>
                    <div className="effectiveness-bar">
                      <div 
                        className="effectiveness-fill" 
                        style={{ width: `${(effectiveness / 10) * 100}%` }}
                      ></div>
                    </div>
                    <span className="effectiveness-score">{effectiveness.toFixed(1)}/10</span>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* Predicciones y Recomendaciones */}
        <div className="analytics-card recommendations-card">
          <h3>🔮 Predicciones y Recomendaciones</h3>
          
          {analyticsData.predictions.riskDays.length > 0 && (
            <div className="predictions-section">
              <h4>⚠️ Días de mayor riesgo:</h4>
              <div className="risk-days">
                {analyticsData.predictions.riskDays.map((day, index) => (
                  <span key={index} className="risk-day-tag">{day}</span>
                ))}
              </div>
            </div>
          )}

          <div className="recommendations-section">
            <h4>💡 Recomendaciones:</h4>
            <ul className="recommendations-list">
              {analyticsData.predictions.recommendations.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedDashboard;